/* Import External Modules */
import React from 'react';

/* Import Leverege Modules */
import { ns } from '@leverege/i18n';
import { Button, Flex, PropertyGrid, Selector, Text, TextInput } from '@leverege/ui-elements';
import { useValueChange } from '@leverege/ui-hooks';
/* Import Local Modules */
import UnitFormatter from "./TimestampFormatter.js";
import Model from "./TimestampFormatterModel.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const {
  t
} = ns('ui-attributes.TimestampFormatterEditor');

/**
 * Editor for timestamp formatter.
 * @param {Object} props.value - Value formatter object.
 * @param {String} props.value.type - Value type, e.g. attr.formatter.timestamp.
 */
export default function TimestampFormatterEditor(props) {
  // Get parameters
  const {
    value
  } = props;
  const onModelChange = useValueChange(Model, props);
  const now = new Date();
  const templates = [];

  // Validate parameters
  if (value == null) {
    return null;
  }

  // Set selection to not-set when value is null or empty
  if (!value?.format) {
    templates.push({
      value: value?.format,
      name: t('notSet')
    });
  }

  // Initialize date-fns compatible templates, see https://date-fns.org/v2.15.0/docs/format
  templates.push(...[{
    value: 'M/d',
    name: t('shortDate')
  }, {
    value: 'P',
    name: t('longDate')
  }, {
    value: 'h:mm a',
    name: t('shortTime')
  }, {
    value: 'pp',
    name: t('longTime')
  }, {
    value: 'M/d h:mm a',
    name: t('shortDateAndTime')
  }, {
    value: 'Ppp',
    name: t('longDateAndTime')
  }, {
    value: 'T',
    name: t('msTimestamp')
  }, {
    value: 'iso8601',
    name: t('iso')
  },
  // https://date-fns.org/v2.15.0/docs/formatISO
  {
    value: 'fromNow',
    name: t('from')
  } // https://date-fns.org/v2.15.0/docs/formatDistanceToNow
  ]);

  // Add examples to templates
  templates.forEach(template => {
    if (!template.value) {
      return;
    }
    const example = UnitFormatter.format(now, {
      format: template.value
    })[0];
    template.name += t('example', {
      example
    });
  });

  // Set selection to custom when using non-templated format
  if (value?.format && !templates.map(template => template.value).includes(value?.format)) {
    templates.push({
      value: value?.format,
      name: t('Custom')
    });
  }

  // Get format preview
  let preview = t('notAvailable');
  try {
    const rawPreview = UnitFormatter.format(now, value);
    preview = rawPreview.length > 0 ? rawPreview[0] : preview;
  } catch (error) {
    preview = t('invalidFormat');
  }

  // Render editor
  return /*#__PURE__*/_jsxs(PropertyGrid, {
    style: {
      backgroundColor: 'var(--color-white)',
      border: 0
    },
    children: [/*#__PURE__*/_jsx(PropertyGrid.Item, {
      stretch: true,
      label: t('dateTimeFormat'),
      children: /*#__PURE__*/_jsx(Selector, {
        eventData: "setFormat",
        value: Model.getFormat(value),
        values: templates,
        onChange: onModelChange
      })
    }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
      stretch: true,
      label: t('formatCode'),
      children: /*#__PURE__*/_jsxs(Flex, {
        variant: "rowM",
        align: "center",
        children: [/*#__PURE__*/_jsx(TextInput, {
          size: 24,
          eventData: "setFormat",
          hint: t('formatCodeHint'),
          value: Model.getFormat(value),
          onChange: onModelChange
        }), /*#__PURE__*/_jsx(Button, {
          variant: "tertiary",
          icon: "fa fa-external-link",
          onClick: () => window.open('https://date-fns.org/v2.15.0/docs/format'),
          children: t('viewDocs')
        })]
      })
    }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
      fill: true,
      label: t('preview'),
      children: /*#__PURE__*/_jsx(Text, {
        children: preview
      })
    })]
  });
}